/* 
🎯 Shell UI Design System v3.0 - Dot Matrix Component
Purpose: Hero number displays with retro calculator/LED aesthetic
Philosophy: Retro-tech nostalgia, oversized confidence, playful brutalism
*/

import React, { useState, useEffect } from 'react';

export const DotMatrix = ({ 
  value,
  size = 'default',
  animated = false,
  glitchEffect = false,
  className = '',
  ...props 
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    if (animated && value !== displayValue) {
      animateValueChange(value);
    } else {
      setDisplayValue(value);
    }
  }, [value, animated, displayValue]);

  const animateValueChange = (newValue) => {
    // Simple counting animation for numeric values
    if (typeof displayValue === 'number' && typeof newValue === 'number') {
      const start = displayValue;
      const end = newValue;
      const duration = 1000;
      const steps = Math.abs(end - start);
      const stepDuration = duration / Math.max(steps, 1);

      let current = start;
      const interval = setInterval(() => {
        if (current === end) {
          clearInterval(interval);
          return;
        }
        
        current += current < end ? 1 : -1;
        setDisplayValue(current);
      }, stepDuration);
    } else {
      setDisplayValue(newValue);
    }
  };

  const triggerGlitch = () => {
    if (!glitchEffect) return;
    
    setIsGlitching(true);
    setTimeout(() => setIsGlitching(false), 300);
  };

  const baseClass = 'shell-dotmatrix';
  const sizeClass = size !== 'default' ? `${baseClass}--${size}` : '';
  const glitchClass = isGlitching ? `${baseClass}--glitch` : '';
  
  const combinedClassName = [baseClass, sizeClass, glitchClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div 
      className={combinedClassName}
      onClick={triggerGlitch}
      {...props}
    >
      <span className="shell-dotmatrix__content">
        {displayValue}
      </span>
    </div>
  );
};

export const DotMatrixClock = ({ 
  format24 = true,
  showSeconds = true,
  className = '',
  ...props 
}) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (date) => {
    const hours = format24 
      ? date.getHours().toString().padStart(2, '0')
      : ((date.getHours() % 12) || 12).toString().padStart(2, '0');
    
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    
    const timeString = showSeconds 
      ? `${hours}:${minutes}:${seconds}`
      : `${hours}:${minutes}`;
    
    return format24 ? timeString : `${timeString} ${date.getHours() >= 12 ? 'PM' : 'AM'}`;
  };

  return (
    <DotMatrix 
      value={formatTime(time)}
      size="large"
      className={className}
      {...props}
    />
  );
};

export const DotMatrixCounter = ({ 
  from = 0,
  to,
  duration = 2000,
  autoStart = true,
  onComplete,
  className = '',
  ...props 
}) => {
  const [count, setCount] = useState(from);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (autoStart && to !== undefined) {
      startCounting();
    }
  }, [autoStart, to]);

  const startCounting = () => {
    if (to === undefined) return;
    
    setIsAnimating(true);
    const steps = Math.abs(to - from);
    const stepDuration = duration / Math.max(steps, 1);
    
    let current = from;
    const interval = setInterval(() => {
      if (current === to) {
        clearInterval(interval);
        setIsAnimating(false);
        if (onComplete) onComplete();
        return;
      }
      
      current += current < to ? 1 : -1;
      setCount(current);
    }, stepDuration);
  };

  return (
    <DotMatrix 
      value={count}
      animated={isAnimating}
      className={className}
      {...props}
    />
  );
};

export const DotMatrixStatus = ({ 
  status,
  className = '',
  ...props 
}) => {
  const getStatusDisplay = () => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'active':
      case 'ready':
        return 'ONLINE';
      case 'offline':
      case 'inactive':
        return 'OFFLINE';
      case 'error':
      case 'failed':
        return 'ERROR';
      case 'loading':
      case 'pending':
        return 'LOADING';
      default:
        return status?.toUpperCase() || 'STATUS';
    }
  };

  const getStatusClass = () => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'active':
      case 'ready':
        return 'shell-dotmatrix--success';
      case 'error':
      case 'failed':
        return 'shell-dotmatrix--error';
      case 'loading':
      case 'pending':
        return 'shell-dotmatrix--warning';
      default:
        return '';
    }
  };

  return (
    <DotMatrix 
      value={getStatusDisplay()}
      glitchEffect={status === 'error'}
      className={`${getStatusClass()} ${className}`}
      {...props}
    />
  );
};

export const DotMatrixGrid = ({ 
  children,
  columns = 'auto-fit',
  gap = 'default',
  className = '',
  ...props 
}) => {
  const baseClass = 'shell-dotmatrix-grid';
  const gapClass = gap !== 'default' ? `${baseClass}--gap-${gap}` : '';
  
  const combinedClassName = [baseClass, gapClass, className]
    .filter(Boolean)
    .join(' ');

  const gridStyle = {
    gridTemplateColumns: typeof columns === 'number' 
      ? `repeat(${columns}, 1fr)`
      : `repeat(${columns}, minmax(200px, 1fr))`
  };

  return (
    <div 
      className={combinedClassName}
      style={gridStyle}
      {...props}
    >
      {children}
    </div>
  );
};

export default DotMatrix;

/*
USAGE EXAMPLES:

// Basic dot matrix display
<DotMatrix value="24:07" size="large" />

// Animated counter
<DotMatrix value={count} animated={true} />

// Clock component
<DotMatrixClock format24={true} showSeconds={true} />

// Counter with animation
<DotMatrixCounter 
  from={0} 
  to={100} 
  duration={3000}
  onComplete={() => console.log('Complete!')}
/>

// Status display
<DotMatrixStatus status="online" />
<DotMatrixStatus status="error" />

// Glitch effect for errors
<DotMatrix 
  value="ERROR" 
  glitchEffect={true}
  onClick={() => setHasError(false)}
/>

// Grid layout for multiple displays
<DotMatrixGrid columns={3} gap="large">
  <DotMatrix value="CPU: 45%" />
  <DotMatrix value="MEM: 72%" />
  <DotMatrix value="DISK: 23%" />
</DotMatrixGrid>

// Server metrics dashboard
const ServerMetrics = () => {
  const [metrics, setMetrics] = useState({
    uptime: '04:23:15',
    connections: 142,
    status: 'online'
  });
  
  return (
    <DotMatrixGrid columns={3}>
      <DotMatrix value={metrics.uptime} size="small" />
      <DotMatrixCounter to={metrics.connections} />
      <DotMatrixStatus status={metrics.status} />
    </DotMatrixGrid>
  );
};

FEATURES:
- Large, confident number displays
- Retro calculator/LED aesthetic
- Animated value transitions
- Glitch effects for error states
- Clock and counter variants
- Status display modes
- Grid layout system
- Multiple size options
- Interactive glitch trigger

PHILOSOPHY ALIGNMENT:
- Oversized & confident display
- Retro-tech aesthetic reference
- Playful interaction (glitch effect)
- Functional information display
- Brutalist honesty in data presentation
- Calculator/terminal nostalgia

CSS INTEGRATION:
Uses .shell-dotmatrix classes defined in components.css
- Background contrast for readability
- Monospace font for authentic feel
- Large size for confident presentation
- Animation support for dynamic values
*/